import React from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { IKImage } from 'imagekitio-react';
import { AnimationComponent } from '../../Animations';
import { useActionExectioner } from '../core/actions';
import { HotspotTrigger } from '../../../components/hotspot';
import { getDropShadow, getBoxShadow } from '../../../services/cssHelper.service';

import { POSITION } from './../../../constants/background';

import styles from './image.module.scss';

const ImageWidgetComponent = ({ className, style, widgetProps, breakpoint, onClick }) => {
  const actionExcutioner = useActionExectioner();

  const ref = React.useRef();

  const id = _.get(widgetProps, 'uid');
  const crop = _.get(widgetProps, `properties.${breakpoint}.crop`);

  const animation = _.get(widgetProps, `properties.${breakpoint}.animation`) || {};
  const content = _.get(widgetProps, 'content');
  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);
  const url = _.get(content, 'url');
  const alt = _.get(widgetProps, `properties.desktop.alt`, '');
  const action = _.get(widgetProps, 'actions.0');

  const border = _.get(widgetProps, `properties.${breakpoint}.border`) || 0;
  const boxShadow = _.get(widgetProps, `properties.${breakpoint}.shadow`, null);
  const width = parseFloat(layout.width);
  const height = parseFloat(layout.height);

  const handleClick = onClick ? onClick : (e) => {
    if (action) {
      e.stopPropagation();
      actionExcutioner.execute(widgetProps, action);
    }
  }

  let widgetStyle = {
    ...style
  }
  let maskStyle = {}

  if(action) {
    widgetStyle = {
      ...widgetStyle,
      cursor: 'pointer'
    }
  }

  const imageStyles = {
    ...getStyles(widgetProps, breakpoint, crop)
  }

  if(crop){
    const borderStyle = getBorderStyles(border);
    const shadow =  getBoxShadow(boxShadow);

    maskStyle = {
        ...borderStyle,
        ...shadow
      }
  }

  return (
    <HotspotTrigger widget={widgetProps} breakpoint={breakpoint} targetRef={ref} >
      <div id={id}
        ref={ref}
        className={cx(className, styles.image, 'image_widget')}
        style={widgetStyle}
        data-widget-id={id}
        onClick={handleClick}
        >
        <AnimationComponent {...animation}>
          <div className={styles.maskView} style={maskStyle}>
            <ImageView
              breakpoint={breakpoint}
              content={content}
              alt={alt}
              width={width}
              height={height}
              style={imageStyles}
              crop={crop}
            />
          </div>
        </AnimationComponent>
      </div>
    </HotspotTrigger>

  );
}

export const ImageView = ({ breakpoint, content, alt, width, height, crop, style }) => {
  const url = _.get(content, 'url');
  const imageWidth = _.get(content, 'width');
  const imageHeight = _.get(content, 'height');

  const [styles, setStyles] = React.useState({
    ...style,
    width: width,
    height: height,
    maskImage: crop && crop.mask ? `url(${crop.mask})` : "unset",
    WebkitMaskImage: crop && crop.mask ? `url(${crop.mask})` : "unset"
  })

  var transformation = [];

  if (crop) {
    transformation.push({
      width: 2 * crop.zoomWidth,
      height: 2 * crop.zoomHeight,
      crop: 'force',
    });

    transformation.push({
      width: 2 * crop.width,
      height: 2 * crop.height,
      cropMode: 'extract',
      x: 2 * crop.x,
      y: 2 * crop.y
    });
  }else if(imageWidth > 0 && crop){
    var cropWidth = parseInt(width) * 2;
    cropWidth = Math.min(cropWidth, imageWidth);

    transformation.push({
      width: cropWidth,
      x: 2 * crop.x,
      y: 2 * crop.y
    });
  }

  var cropWidth = parseInt(width) * 2;
  var cropHeight = parseInt(height) * 2;

  cropWidth = Math.min(cropWidth, imageWidth);
  cropHeight = Math.min(cropHeight, imageHeight);

  transformation.push({
    width: cropWidth,
    height: cropHeight,
    crop: 'force'
  });

  transformation.push({
    quality: 100,
    format: 'webp'
  });

  React.useEffect(() => {
    setStyles(styles => {
      return {
        ...styles,
        width: width,
        height: height,
        maskImage: crop && crop.mask ? `url(${crop.mask})` : "unset",
        WebkitMaskImage: crop && crop.mask ? `url(${crop.mask})` : "unset"
      }
    })
  }, [width, height, crop])

  return (
    <IKImage
      path={url}
      alt={alt}
      width={width}
      height={height}
      transformation={transformation}
      lqip={{ active: true }}
      style={{
        ...style,
        width: "100%",
        height: "100%",
        maskImage: crop && crop.mask ? `url(${crop.mask})` : "unset",
        borderStyle: crop ? "none" : "solid"
      }}
    />
  );
}

/**
 * Generate Styles
 *
*/
function getStyles(properties, breakpoint, crop) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const deviceProperties = _.get(properties, `properties.${breakpoint}`);

  const topLeft = _.get(deviceProperties, 'border.radius.topLeft', null);
  const topRight = _.get(deviceProperties, 'border.radius.topRight', null);
  const bottomLeft = _.get(deviceProperties, 'border.radius.bottomLeft', null);
  const bottomRight = _.get(deviceProperties, 'border.radius.bottomRight', null);

  if(!crop){

    if (topLeft) {
      styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
    }

    if (topRight) {
      styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
    }

    if (bottomLeft) {
      styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
    }

    if (bottomRight) {
      styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
    }

    const colorRgb = _.get(deviceProperties, 'border.color');

    var color = 'transparent';
    if (colorRgb) {
      color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
    }

    styles['borderColor'] = color;

    const borderWidthString = _.get(deviceProperties, 'border.width') || "0";
    var borderWidget = parseInt(borderWidthString) + 'px';

    styles['borderWidth'] = borderWidget;
    styles['borderStyle'] = "solid"

    const objectPosition = getImagePosition(_.get(deviceProperties, 'position'));
    styles['objectPosition'] = objectPosition;

    //DropShadow
    const shadow = _.get(deviceProperties, `shadow`, {});
    const shadowStyle = getDropShadow(shadow);
    styles = {
      ...styles,
      ...shadowStyle
    }
  }
    return styles;
  
}

function getImagePosition(position) {
  var backgroundPosition = 'center center';

  switch (position) {
    case POSITION.TOP_LEFT:
      backgroundPosition = 'left top';
      break;

    case POSITION.TOP_CENTER:
      backgroundPosition = 'center top';
      break;

    case POSITION.TOP_RIGHT:
      backgroundPosition = 'right top';
      break;

    case POSITION.CENTER_LEFT:
      backgroundPosition = 'left center';
      break;

    case POSITION.CENTER:
      backgroundPosition = 'center center';
      break;

    case POSITION.CENTER_RIGHT:
      backgroundPosition = 'right center';
      break;

    case POSITION.BOTTOM_LEFT:
      backgroundPosition = 'left bottom';
      break;

    case POSITION.BOTTOM_CENTER:
      backgroundPosition = 'center bottom';
      break;

    case POSITION.BOTTOM_RIGHT:
      backgroundPosition = 'right bottom';
      break;
  }

  return backgroundPosition;
}

function getBorderStyles(value) {
  if (!value) {
    return null;
  }

  var styles = {};
  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  const colorRgb = _.get(value, 'color', "transparent");

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;
  styles['backgroundColor'] = color;
  styles['borderStyle'] = "solid";
  styles['overflow'] = "hidden";

  return styles;
}

const imageWidgetEquals = function(prevProps, nextProps){
  const paths = ['className', 'style', 'widgetProps', 'breakpoint'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

export const ImageWidget = React.memo(ImageWidgetComponent, imageWidgetEquals);
